import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { storageApi, uploadApi } from '../utils/api';
import { File, UserStorage, StoragePrice, StoragePurchase } from '../types';
import toast from 'react-hot-toast';
import { Copy, Check } from 'lucide-react';
import StorageExpansionModal from '../components/StorageExpansionModal';
import { useLanguageStore } from '../stores/languageStore';

const StoragePage: React.FC = () => {
  const { language } = useLanguageStore();
  const t = (key: string) => {
    const { t: translate } = useLanguageStore();
    return translate(key);
  };
  const [storageInfo, setStorageInfo] = useState<UserStorage | null>({
    total_space: 536870912,
    used_space: 0,
    available_space: 536870912,
    total_space_formatted: '500MB',
    used_space_formatted: '0B',
    available_space_formatted: '500MB',
  } as any);
  const [files, setFiles] = useState<File[]>([]);
  const [purchases, setPurchases] = useState<StoragePurchase[]>([]);
  const [prices, setPrices] = useState<StoragePrice[]>([
    { id: 1, space_amount: 536870912, price: 4.9, description: '500MB空间', is_active: true, sort_order: 1, space_amount_formatted: '500MB' },
    { id: 2, space_amount: 1073741824, price: 9.9, description: '1GB空间', is_active: true, sort_order: 2, space_amount_formatted: '1GB' },
  ]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState<'files' | 'purchases'>('files');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileToDelete, setFileToDelete] = useState<number | null>(null);
  const [showExpansionModal, setShowExpansionModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      try {
        const storageRes = await storageApi.getStorageInfo();
        if (storageRes.success && storageRes.data) {
          setStorageInfo(storageRes.data);
        }
      } catch (e) {
        console.error('Failed to load storage info:', e);
        setStorageInfo({
          total_space: 536870912,
          used_space: 0,
          available_space: 536870912,
          total_space_formatted: '500MB',
          used_space_formatted: '0B',
          available_space_formatted: '500MB',
        } as any);
      }

      try {
        const filesRes = await storageApi.getFileList();
        if (filesRes.success && filesRes.data) {
          setFiles(filesRes.data.files);
        }
      } catch (e) {
        console.error('Failed to load files:', e);
        setFiles([]);
      }

      try {
        const pricesRes = await storageApi.getPriceList();
        if (pricesRes.success && pricesRes.data) {
          setPrices(pricesRes.data);
        }
      } catch (e) {
        console.error('Failed to load prices:', e);
      }

      try {
        const purchasesRes = await storageApi.getPurchaseHistory();
        if (purchasesRes.success && purchasesRes.data) {
          setPurchases(purchasesRes.data.purchases);
        }
      } catch (e) {
        console.error('Failed to load purchases:', e);
        setPurchases([]);
      }

    } catch (error: any) {
      console.error('Failed to load storage data:', error);
      toast.error(error.response?.data?.message || t('storage.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      console.log('📁 选择了文件:', file.name, '大小:', file.size, '类型:', file.type);
      setSelectedFile(file);
      toast.success(`✅ 已选择: ${file.name}`);
    } else {
      console.log('📁 没有选择文件');
      setSelectedFile(null);
    }
  };

  const handleUpload = async () => {
    console.log('🚀 点击上传按钮 - selectedFile:', selectedFile ? selectedFile.name : null, 'uploading:', uploading);

    if (!selectedFile) {
      console.log('❌ 没有选择文件');
      toast.error(t('storage.selectFile'));
      return;
    }

    if (selectedFile.size > 100 * 1024 * 1024) {
      console.log('❌ 文件太大:', selectedFile.size);
      toast.error(t('storage.fileTooLarge'));
      return;
    }

    try {
      console.log('📤 开始上传:', selectedFile.name);
      setUploading(true);

      const res = await uploadApi.uploadAttachment(selectedFile);
      console.log('📥 上传响应:', res);

      if (res.success) {
        console.log('✅ 上传成功');
        toast.success(t('storage.uploadSuccess'));
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        loadData();
      } else {
        console.log('❌ 上传失败:', res.message);
        toast.error(res.message || t('storage.uploadFailed'));
      }
    } catch (error: any) {
      console.error('❌ 上传异常:', error);
      toast.error(error.response?.data?.message || error.message || t('storage.uploadFailed'));
    } finally {
      console.log('🔄 上传结束，重置状态');
      setUploading(false);
    }
  };

  const handleDeleteFile = (fileId: number) => {
    setFileToDelete(fileId);
  };

  const confirmDeleteFile = async () => {
    if (!fileToDelete) return;

    try {
      const res = await storageApi.deleteFile(fileToDelete);
      if (res.success) {
        toast.success(t('storage.deleteSuccess'));
        loadData();
      }
    } catch (error) {
      console.error('Failed to delete file:', error);
      toast.error(t('storage.deleteFailed'));
    } finally {
      setFileToDelete(null);
    }
  };

  const cancelDeleteFile = () => {
    setFileToDelete(null);
  };

  const handleConfirmPayment = async (purchaseId: number) => {
    if (!window.confirm(t('storage.confirmPayment'))) return;

    try {
      const res = await storageApi.confirmPayment(purchaseId);
      if (res.success) {
        toast.success(`${t('storage.paymentSuccess')} ${res.data?.space_added_formatted}`);
        loadData();
      }
    } catch (error: any) {
      console.error('Failed to confirm payment:', error);
      toast.error(error.response?.data?.message || t('storage.confirmPaymentFailed'));
    }
  };

  const getFileIcon = (fileExt: string) => {
    const ext = fileExt.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) {
      return '🖼️';
    } else if (['pdf'].includes(ext)) {
      return '📄';
    } else if (['doc', 'docx'].includes(ext)) {
      return '📝';
    } else if (['xls', 'xlsx'].includes(ext)) {
      return '📊';
    } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      return '📦';
    } else if (['txt'].includes(ext)) {
      return '📃';
    } else {
      return '📁';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">已支付</span>;
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">待支付</span>;
      case 'failed':
        return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">失败</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">{status}</span>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <Link to="/" className="text-pink-600 hover:text-pink-800">
              ← 返回首页
            </Link>
            {loading && (
              <div className="flex items-center text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-pink-500 mr-2"></div>
                正在刷新数据...
              </div>
            )}
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">🌸 个人仓库</h1>
          <p className="text-gray-600">管理您的文件和存储空间</p>
        </div>

        {/* 存储空间概览 */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">💾 存储空间</h2>
          {storageInfo ? (
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">已使用</span>
                <span className="text-gray-800 font-medium">{storageInfo.used_space_formatted} / {storageInfo.total_space_formatted}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                <div
                  className="bg-gradient-to-r from-pink-500 to-purple-500 h-4 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min((storageInfo.used_space / storageInfo.total_space) * 100, 100)}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">剩余空间</span>
                <span className="text-green-600 font-medium">{storageInfo.available_space_formatted}</span>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => setShowExpansionModal(true)}
                  className="px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg hover:from-pink-600 hover:to-purple-600 transition-colors font-medium shadow-md"
                >
                  ⬆️ 升级容量
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-500">正在加载存储空间信息...</p>
            </div>
          )}
        </div>

        {/* 标签页导航 */}
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setActiveTab('files')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'files' ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-lg' : 'bg-white text-gray-600 hover:bg-pink-50'
            }`}
          >
            📁 我的文件
          </button>
          <button
            onClick={() => setActiveTab('purchases')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'purchases' ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-lg' : 'bg-white text-gray-600 hover:bg-pink-50'
            }`}
          >
            📜 购买记录
          </button>
        </div>

        {/* 文件列表 */}
        {activeTab === 'files' && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">📁 文件列表</h2>

            {/* 上传区域 */}
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-pink-400 transition-colors mb-6">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                disabled={uploading}
                className="hidden"
                id="storage-file-input"
              />
              <label htmlFor="storage-file-input" className="cursor-pointer">
                <div className="text-6xl mb-4">📁</div>
                <p className="text-lg font-medium text-gray-700 mb-2">
                  {selectedFile ? selectedFile.name : '点击选择文件'}
                </p>
                {selectedFile ? (
                  <p className="text-sm text-gray-500">
                    大小: {(selectedFile.size / 1024).toFixed(2)} KB
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 mt-2">支持所有文件，最大100MB</p>
                )}
              </label>
            </div>

            {/* 上传按钮 */}
            <div className="flex justify-center mb-6">
              <button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                className={`px-8 py-3 rounded-xl font-bold text-lg transition-all ${
                  !selectedFile || uploading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:from-pink-600 hover:to-purple-600 shadow-lg hover:shadow-xl'
                }`}
              >
                {uploading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    上传中...
                  </span>
                ) : (
                  '🚀 开始上传'
                )}
              </button>
            </div>

            {/* 文件列表 */}
            {files.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-4xl mb-4">📂</p>
                <p>暂无文件，点击上方区域上传</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">文件</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">大小</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">下载次数</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">上传时间</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((file) => (
                      <tr key={file.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center">
                            <span className="text-2xl mr-3">{getFileIcon(file.file_ext)}</span>
                            <div>
                              <p className="font-medium text-gray-800">{file.filename}</p>
                              <p className="text-sm text-gray-500">{file.file_type}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-600">{file.file_size_formatted}</td>
                        <td className="py-3 px-4 text-gray-600">{file.download_count}</td>
                        <td className="py-3 px-4 text-gray-600">{new Date(file.created_at).toLocaleDateString()}</td>
                        <td className="py-3 px-4">
                          <a
                            href={storageApi.downloadFile(file.id)}
                            className="text-pink-600 hover:text-pink-800 mr-3"
                          >
                            下载
                          </a>
                          <button
                            onClick={() => handleDeleteFile(file.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            删除
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* 购买记录 */}
        {activeTab === 'purchases' && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">📜 购买记录</h2>

            {purchases.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-4xl mb-4">📜</p>
                <p>暂无购买记录</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">容量</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">价格</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">状态</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">购买时间</th>
                      <th className="text-left py-3 px-4 text-gray-600 font-medium">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchases.map((purchase) => (
                      <tr key={purchase.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 text-gray-800 font-medium">+{purchase.space_added_formatted}</td>
                        <td className="py-3 px-4 text-gray-600">¥{Number(purchase.price).toFixed(2)}</td>
                        <td className="py-3 px-4">{getStatusBadge(purchase.payment_status)}</td>
                        <td className="py-3 px-4 text-gray-600">{new Date(purchase.created_at).toLocaleString()}</td>
                        <td className="py-3 px-4">
                          {purchase.payment_status === 'pending' && (
                            <button
                              onClick={() => purchase.id && handleConfirmPayment(purchase.id)}
                              className="px-3 py-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-md hover:from-pink-600 hover:to-purple-600 transition-colors text-sm"
                            >
                              确认支付
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 删除确认模态框 */}
      {fileToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-gray-800 mb-4">确认删除</h3>
            <p className="text-gray-600 mb-6">确定要删除这个文件吗？此操作无法撤销。</p>
            <div className="flex gap-3">
              <button
                onClick={confirmDeleteFile}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                删除
              </button>
              <button
                onClick={cancelDeleteFile}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 升级容量模态框 */}
      <StorageExpansionModal
        isOpen={showExpansionModal}
        onClose={() => setShowExpansionModal(false)}
        onSuccess={loadData}
      />
    </div>
  );
};

export default StoragePage;
